/* -------------------------------------------------------------------------- */
/*                        Date picker styles                                  */
/* -------------------------------------------------------------------------- */

@import './bq-date-picker.variables';

:host {
  @apply block is-full;
}

/* -------------------------------------------------------------------------- */
/*                                    Label                                   */
/* -------------------------------------------------------------------------- */

.bq-date-picker__label {
  @apply flex flex-grow items-center gap-[--bq-date-picker--gap] m-be-[--bq-date-picker--label-margin-bottom];
  @apply text-[length:--bq-date-picker--label-text-size] text-[color:--bq-date-picker--label-text-color];
}

/* -------------------------------------------------------------------------- */
/*                         Date picker input group control                    */
/* -------------------------------------------------------------------------- */

.bq-date-picker__control {
  @apply flex items-center transition-[border-color,box-shadow] is-full;
  // Border
  @apply rounded-[--bq-date-picker--border-radius] border-[length:--bq-date-picker--border-width] border-[color:--bq-date-picker--border-color];
  // Padding
  @apply pe-[--bq-date-picker--padding-end] ps-[--bq-date-picker--padding-start] p-b-[--bq-date-picker--paddingY];
  // Text
  @apply select-none text-[length:--bq-date-picker--text-size] text-[color:--bq-date-picker--text-color] placeholder:text-[color:--bq-date-picker--text-placeholder-color];
  // Hover
  @apply [&:not(.disabled):not(:focus-within)]:hover:border-hover-brand;

  border-style: var(--bq-date-picker--border-style);

  // Focus
  &:not(.disabled):focus-within {
    --bq-ring-width: 1px;
    --bq-ring-offset-width: 0;

    @apply focus border-[color:--bq-focus];
  }

  // Enable clear button whenever the input group control is hovered or has focus
  &:not(.disabled):hover,
  &:not(.disabled):focus-within {
    .bq-date-picker__control--clear {
      @apply inline-block;
    }
  }
}

.bq-date-picker__control.disabled {
  @apply cursor-not-allowed opacity-60;
}

/* ------------------------------- Validation ------------------------------- */

.bq-date-picker__control.validation-error {
  @apply border-danger [&:not(.disabled):not(:focus-within)]:hover:border-hover-danger;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.danger);

    @apply border-active-danger;
  }
}

.bq-date-picker__control.validation-success {
  @apply border-success [&:not(.disabled):not(:focus-within)]:hover:border-hover-success;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.success);

    @apply border-active-success;
  }
}

.bq-date-picker__control.validation-warning {
  @apply border-warning [&:not(.disabled):not(:focus-within)]:hover:border-hover-warning;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.warning);

    @apply border-active-warning;
  }
}

/* -------------------------------------------------------------------------- */
/*                              Native HTML Input                             */
/* -------------------------------------------------------------------------- */

.bq-date-picker__control--input {
  @apply flex-auto cursor-inherit select-none appearance-none bg-inherit font-inherit text-[length:inherit] text-inherit;
  @apply border-none min-is-0 p-b-0 p-i-0 m-b-0 m-i-0 focus:outline-none focus-visible:outline-none;
}

/* -------------------------------------------------------------------------- */
/*                                Clear button                                */
/* -------------------------------------------------------------------------- */

.bq-date-picker__control--clear::part(button) {
  // Since the clear button is inside the input group control,
  // we need to reset the focus ring styles
  --bq-ring-width: initial;
  --bq-ring-offset-width: initial;
  --bq-ring-color-focus: initial;
}

/* -------------------------------------------------------------------------- */
/*                              Prefix and suffix                             */
/* -------------------------------------------------------------------------- */

.bq-date-picker__control--prefix,
.bq-date-picker__control--suffix {
  @apply pointer-events-none flex items-center text-[color:--bq-date-picker--text-color];
}

.bq-date-picker__control--prefix {
  @apply me-[--bq-date-picker--gap];
}

.bq-date-picker__control--suffix {
  @apply ms-[--bq-date-picker--gap];
}

/* -------------------------------------------------------------------------- */
/*                         Customize Cally date picker                         */
/* -------------------------------------------------------------------------- */

calendar-date,
calendar-range,
calendar-multi {
  &::part(header) {
    @apply justify-center gap-s;
  }

  &::part(heading) {
    @apply p-b-xs2 p-i-xs;
  }

  &::part(button) {
    @apply rounded-s border-0 bg-transparent transition-colors duration-200 p-b-xs2 p-i-xs2;
    @apply focus-visible:focus hover:bg-hover-ui-primary;
  }

  &::part(container) {
    @apply bg-[--bq-date-picker--background-color] p-b-0 p-i-0;
  }
}

calendar-month {
  /* stylelint-disable-next-line custom-property-pattern */
  --color-accent: var(--bq-ui--brand);

  &::part(button) {
    @apply flex items-center justify-center rounded-s bg-transparent font-default text-primary transition-colors duration-200 ease-in-out p-b-0 p-i-0;
    @apply size-[--bq-date-picker--day-size] focus-visible:focus enabled:hover:bg-hover-secondary disabled:cursor-not-allowed;
  }

  &::part(button day disallowed) {
    @apply cursor-not-allowed bg-transparent #{!important};
  }

  &::part(button day selected) {
    @apply rounded-s bg-[--bq-date-picker--range-background-color] text-alt enabled:hover:bg-hover-ui-brand;
  }

  &::part(button day selected range-inner) {
    @apply rounded-none bg-[--bq-date-picker--range-inner-background-color] text-primary hover:text-alt;
  }

  &::part(button day outside) {
    @apply cursor-pointer text-secondary opacity-100;
  }

  &::part(today) {
    @apply rounded-s border-[length:--bq-date-picker--currentDate-border-width] border-[color:--bq-date-picker--currentDate-border-color];

    border-style: var(--bq-date-picker--border-style);
  }

  &::part(today selected) {
    @apply text-alt;
  }

  &::part(today selected range-inner) {
    /* Ensure 'today selected range-inner' keeps the intended color */
    @apply text-primary;
  }

  &::part(today range-inner) {
    @apply rounded-none;
  }

  &::part(today range-start) {
    @apply rounded-s rounded-es-none;
  }

  &::part(today range-end) {
    @apply rounded-s rounded-ee-none;
  }

  &::part(range-start) {
    @apply rounded-ee-none rounded-se-none;
  }

  &::part(range-end) {
    @apply rounded-es-none rounded-ss-none;
  }

  &::part(range-start range-end) {
    @apply rounded-s;
  }

  &::part(table) {
    @apply border-spacing-0 text-[length:_inherit];
  }
}
